import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FnbPosContext, FnbPosContextValue, FnbPosOutlet, FnbPosOutletCreateData } from '@riptik/fnbpos-backoffice-module';
import { useTenant } from '../contexts/TenantContext';
import { useTranslation } from '../hooks/useTranslation';
import { useEventContext } from '../contexts/EventContext';
import { getAuthToken, getBaseUrl } from '../api/client';
import { FnbService } from '../api/generated-fnbpos';
import { apiClient } from '../services/apiClient';
import { getWebshopBaseUrl } from '../utils/webshopUrl';
import '../api/client-fnbpos'; // side-effect: cấu hình OpenAPI singleton của generated-fnbpos (BASE/TOKEN/HEADERS)

// Response shape from bo-gateway POST /organizers/:org/cms/images/upload (mirror EventCoverUpload.tsx —
// CÙNG route upload ảnh S3 dùng chung của host, dùng lại chứ không tạo route mới cho P6k Bill Designer).
interface UploadedImage {
  resolutions?: Record<string, { url?: string }>;
}

function pickUploadedUrl(img: UploadedImage): string {
  const r = img.resolutions || {};
  return r.original?.url || r.xlarge?.url || r.large?.url || r.medium?.url || Object.values(r)[0]?.url || '';
}

/**
 * Riptik F&B/POS Provider
 *
 * Provider này THUỘC VỀ backoffice (parent project) — implement FnbPosContextValue bằng client
 * generated-fnbpos (sinh từ fnbpos/docs/openapi.json, xem scripts/rewrite-fnbpos-openapi.mjs).
 * Cùng mẫu RiptikCMSProvider: lấy tenant từ useTenant(), t từ useTranslation(), getOrgUrl từ
 * useEventContext(). Nếu project khác muốn dùng module này, họ chỉ cần viết Provider riêng.
 *
 * tenantId F&B = organization_slug (ánh xạ 1:1, quyết định 2026-07-19) — KHÔNG có bảng mapping riêng.
 * Outlet: host (Provider này) SỞ HỮU cache — module chỉ đọc outlets/outletsLoading + gọi refreshOutlets.
 */
export const FnbPosProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const tenant = useTenant();
  const { t } = useTranslation();
  const { getOrgUrl } = useEventContext();

  const [outlets, setOutlets] = useState<FnbPosOutlet[]>([]);
  const [outletsLoading, setOutletsLoading] = useState(false);
  const [selectedOutletId, setSelectedOutletId] = useState<number | null>(null);

  const organizationSlug = tenant?.organization_slug;

  const fetchOutlets = useCallback(async (): Promise<void> => {
    if (!organizationSlug) return;
    setOutletsLoading(true);
    try {
      const resp = await FnbService.outletsList(organizationSlug, {});
      const rows = Array.isArray(resp?.data) ? (resp.data as unknown[]) : [];
      const mapped: FnbPosOutlet[] = rows.map((r) => {
        const row = r as { id: number; code: string; name: string; group_id?: string | null; active: boolean; ticketing_enabled?: boolean };
        return { id: row.id, code: row.code, name: row.name, groupId: row.group_id, active: row.active, ticketingEnabled: row.ticketing_enabled ?? false };
      });
      setOutlets(mapped);
    } catch (error) {
      console.error('FnbPosProvider: fetch outlets failed', error);
      setOutlets([]);
    } finally {
      setOutletsLoading(false);
    }
  }, [organizationSlug]);

  useEffect(() => {
    void fetchOutlets();
  }, [fetchOutlets]);

  const contextValue = useMemo<FnbPosContextValue | null>(() => {
    if (!organizationSlug) return null;

    return {
      tenantId: organizationSlug,
      organizationSlug,

      locale: 'en',
      availableLocales: tenant?.supported_languages?.map((l: { code: string }) => l.code) || ['en', 'vi'],
      t,

      getOrgUrl,
      // Ticket 131: trang khách gọi món QR nằm ở webshop dưới đường dẫn org (/o/<slug>/t/<token>)
      shopBaseUrl: `${getWebshopBaseUrl()}/o/${organizationSlug}`,

      outlets,
      outletsLoading,
      refreshOutlets: fetchOutlets,
      selectedOutletId,
      selectOutlet: setSelectedOutletId,

      api: {
        listOutlets: async (groupId?: string): Promise<FnbPosOutlet[]> => {
          const resp = await FnbService.outletsList(organizationSlug, groupId ? { group_id: groupId } : {});
          const rows = Array.isArray(resp?.data) ? (resp.data as unknown[]) : [];
          return rows.map((r) => {
            const row = r as { id: number; code: string; name: string; group_id?: string | null; active: boolean; ticketing_enabled?: boolean };
            return { id: row.id, code: row.code, name: row.name, groupId: row.group_id, active: row.active, ticketingEnabled: row.ticketing_enabled ?? false };
          });
        },

        createOutlet: async (data: FnbPosOutletCreateData): Promise<FnbPosOutlet> => {
          const resp = await FnbService.outlets(organizationSlug, {
            code: data.code,
            name: data.name,
            group_id: data.groupId,
          });
          const row = resp?.data as { id: number; code: string; name: string; group_id?: string | null; active: boolean };
          return { id: row.id, code: row.code, name: row.name, groupId: row.group_id, active: row.active };
        },

        // Escape hatch tổng quát (xem FnbPosContextValue.api.request) — gọi trực tiếp qua fetch vì
        // client generated tổ chức theo per-operation method, không có 1 hàm "gọi path bất kỳ".
        request: async <T,>(path: string, body: Record<string, unknown> = {}): Promise<T> => {
          const url = `${getBaseUrl()}/organizers/${encodeURIComponent(organizationSlug)}/fnbpos${path}`;
          const token = getAuthToken();
          const res = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(body),
          });
          const json = await res.json();
          if (!json.success) {
            throw new Error(json.message || json.error || 'fnbpos request failed');
          }
          return json.data as T;
        },

        // P6k (Bill Designer, logo) — dùng LẠI route upload ảnh S3 chung của host (CMS images upload,
        // xem EventCoverUpload.tsx), KHÔNG phải route fnbpos riêng — đây là lý do method này tách khỏi
        // `request()` phía trên (khác gateway path, khác content-type multipart).
        uploadImage: async (file: File): Promise<string> => {
          const formData = new FormData();
          formData.append('file', file);
          const resp = await apiClient.upload<UploadedImage>(
            `/organizers/${encodeURIComponent(organizationSlug)}/cms/images/upload`,
            formData,
          );
          const url = pickUploadedUrl(resp);
          if (!url) throw new Error('No URL in upload response');
          return url;
        },
      },
    };
  }, [organizationSlug, tenant, t, getOrgUrl, outlets, outletsLoading, fetchOutlets, selectedOutletId]);

  if (!contextValue) {
    return null;
  }

  return <FnbPosContext.Provider value={contextValue}>{children}</FnbPosContext.Provider>;
};
